/* eslint-disable react/jsx-props-no-spreading */

import { useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import { SxProps, useTheme } from '@mui/material/styles';
import { TextFieldProps } from '@mui/material/TextField';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  DesktopDatePicker,
  DesktopDatePickerProps,
} from '@mui/x-date-pickers/DesktopDatePicker';
import { CalendarPickerView } from '@mui/x-date-pickers/internals/models/views';
import {
  MobileDatePicker,
  MobileDatePickerProps,
} from '@mui/x-date-pickers/MobileDatePicker';
import dayjs, { Dayjs, isDayjs } from 'dayjs';
import React, { useContext, useMemo, useState } from 'react';
import { ETOIconButtonProps } from '../Button/ETOIconButton';
import { TDatePickersValue } from './DateTimeCommonProps';
import { useCommonClasses } from './DateTimeCommonStyles';
import SelectorTextField from './SelectorTextField';
import { SettingsContext } from '../../contexts';

type DatePickerViewOptionsTypes = 'day' | 'month' | 'year';

export interface DateSelectorProps
  extends Omit<MobileDatePickerProps<Dayjs, Dayjs>, 'value' | 'renderInput'>,
  Omit<DesktopDatePickerProps<Dayjs, Dayjs>, 'value' | 'renderInput'> {
  iconButtonProps?: Omit<ETOIconButtonProps, 'onclick'>;
  containerSx?: SxProps;
  value: TDatePickersValue;
  views?: Partial<DatePickerViewOptionsTypes>[];
  openTo?: DatePickerViewOptionsTypes;
}

const DateSelector = (props: DateSelectorProps) => {
  const {
    inputFormat,
    iconButtonProps,
    onChange,
    containerSx,
    value,
    views,
    openTo,
    ...rest
  } = props;

  const [isOpen, setIsOpen] = useState(false);

  const theme = useTheme();
  const commonStyles = useCommonClasses(theme);

  const { settings } = useContext(SettingsContext);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const _toggle = () => setIsOpen((open) => !open);

  const selectedDate = isDayjs(value) ? value : dayjs(value);

  const commonProps = useMemo(() => ({
    ...rest,
    inputFormat: inputFormat ?? settings.dateFormat,
    onChange: (v: Dayjs | null) => onChange(v),
    onClose: () => setIsOpen(false),
    onOpen: () => setIsOpen(true),
    open: isOpen,
    openTo: openTo ?? ('day' as CalendarPickerView),
    value: selectedDate,
    views: views ?? (['year', 'month', 'day'] as CalendarPickerView[]),
    renderInput: ((param: TextFieldProps) => <SelectorTextField {...param} onKeyDown={(e) => e.preventDefault()} iconButtonProps={iconButtonProps} open={isOpen} onClick={_toggle} />),
  }), [iconButtonProps, inputFormat, isOpen, onChange, openTo, rest, selectedDate, views]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={[
          commonStyles.wrapper,
          ...(Array.isArray(containerSx) ? containerSx : [containerSx]),
        ]}
        data-testid="date wrapper"
      >
        {
          isMobile && (
            <MobileDatePicker
              data-testid="date selector"
              {...commonProps}
            />
          )
        }
        {
          !isMobile && (
            <DesktopDatePicker
              {...commonProps}
            />
          )
        }
      </Box>
    </LocalizationProvider>
  );
};

export default DateSelector;
